import React from 'react';
import { Box, Flex, Heading, Input, List, ListItem, Text, Spinner, Badge, HStack, VStack, Divider, useToast, Button, Collapse, IconButton, Textarea, Select } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { db, signInWithDsCustomToken, appFirebase } from '../utils/firebase.utils';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';

// Utilities to parse telemetry from our stored xmlJson shape
function isNumericKey(k) {
  return /^\d+$/.test(String(k));
}

function normalizeArrayish(value) {
  // Convert array-like objects with numeric keys to real arrays
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const numericKeys = Object.keys(value).filter(isNumericKey).sort((a, b) => Number(a) - Number(b));
    if (numericKeys.length > 0) {
      return numericKeys.map(k => value[k]);
    }
  }
  return [];
}

function extractTelemetryBundle(json) {
  // Find the nearest object that contains both telemetry and telemetryDetails
  if (!json || typeof json !== 'object') return { telemetry: null, telemetryDetails: [] };
  const stack = [json];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur) continue;
    if (cur.telemetry && cur.telemetryDetails) {
      const telemetry = cur.telemetry || null;
      const telemetryDetails = normalizeArrayish(cur.telemetryDetails);
      return { telemetry, telemetryDetails };
    }
    if (Array.isArray(cur)) {
      for (let i = cur.length - 1; i >= 0; i--) stack.push(cur[i]);
    } else {
      for (const k of Object.keys(cur)) {
        const v = cur[k];
        if (v && (typeof v === 'object' || Array.isArray(v))) stack.push(v);
      }
    }
  }
  // Fallback: any telemetry found, and any details found somewhere else
  let firstTelemetry = null;
  const allDetails = [];
  const stack2 = [json];
  while (stack2.length) {
    const cur = stack2.pop();
    if (!cur) continue;
    if (!firstTelemetry && cur.telemetry) firstTelemetry = cur.telemetry;
    if (cur.telemetryDetails) allDetails.push(...normalizeArrayish(cur.telemetryDetails));
    if (Array.isArray(cur)) {
      for (let i = cur.length - 1; i >= 0; i--) stack2.push(cur[i]);
    } else {
      for (const k of Object.keys(cur)) {
        const v = cur[k];
        if (v && (typeof v === 'object' || Array.isArray(v))) stack2.push(v);
      }
    }
  }
  return { telemetry: firstTelemetry, telemetryDetails: allDetails };
}

function toNumber(val) {
  const n = typeof val === 'string' ? parseFloat(val) : (typeof val === 'number' ? val : NaN);
  return Number.isFinite(n) ? n : undefined;
}

function parseDeviceSnapshot(docSnap) {
  const data = docSnap.data() || {};
  const last = data.lastValue || {};
  const { telemetry } = extractTelemetryBundle(last);
  const latitude = toNumber(telemetry?.coordY);
  const longitude = toNumber(telemetry?.coordX);
  const gpsCode = telemetry?.gpsCode != null ? String(telemetry.gpsCode) : docSnap.id;
  const speed = toNumber(telemetry?.speed);
  const date = telemetry?.date || (data.lastValueDateMs ? new Date(data.lastValueDateMs).toISOString() : undefined);
  return {
    id: docSnap.id,
    gpsCode,
    latitude,
    longitude,
    speed,
    date,
    raw: last,
    category: data.category || 'truck',
    markerColor: data.markerColor || '',
    name: data.name || '',
    description: data.description || '',
    groupPath: data.groupPath || '',
  };
}

function SensorAttribute({ label, value }) {
  return (
    <HStack justify="space-between" py={1}>
      <Text color="gray.500">{label}</Text>
      <Text fontWeight="medium">{value}</Text>
    </HStack>
  );
}

// Category display order requested
const CATEGORY_ORDER = [
  'Movimiento y ubicación',
  'Sensores físicos',
  'Vehículo y motor (CANBus / ignición)',
  'Uso y tiempos',
  'Combustible',
  'Rendimiento y consumo',
  'Distancia',
  'Emisiones',
  'Energía',
  'Red y comunicación',
  'Entradas y salidas / analógicos',
  'Eventos y reportes',
  'Información del dispositivo',
  'Otros',
];

// Attribute dictionary -> category mapping
const ATTRIBUTE_CATALOG = {
  // Movimiento y ubicación
  moving_status: { cat: 'Movimiento y ubicación', label: 'movimiento', icon: '🚗' },
  motion_status: { cat: 'Movimiento y ubicación', label: 'estado de movimiento', icon: '🔄' },
  gps_acc: { cat: 'Movimiento y ubicación', label: 'precisión GPS', icon: '🎯' },
  hdop: { cat: 'Movimiento y ubicación', label: 'precisión horizontal', icon: '🎯' },
  pdop: { cat: 'Movimiento y ubicación', label: 'precisión 3D', icon: '🎯' },
  mileage: { cat: 'Movimiento y ubicación', label: 'kilometraje', icon: '🛣️' },
  io_9: { cat: 'Movimiento y ubicación', label: 'odómetro (m)', icon: '🛣️' },
  io_16: { cat: 'Movimiento y ubicación', label: 'contador de pulsos', icon: '🔢' },
  io_19: { cat: 'Movimiento y ubicación', label: 'dirección (°)', icon: '🧭' },
  io_17: { cat: 'Movimiento y ubicación', label: 'altitud (m)', icon: '🏔️' },
  can_speed: { cat: 'Movimiento y ubicación', label: 'velocidad CAN', icon: '🚘' },
  io_6: { cat: 'Movimiento y ubicación', label: 'velocidad', icon: '🚗🛑' },
  can_total_distance: { cat: 'Movimiento y ubicación', label: 'distancia total CAN', icon: '📏' },
  io_2: { cat: 'Movimiento y ubicación', label: 'evento de movimiento', icon: '🚗' },
  io_66: { cat: 'Movimiento y ubicación', label: 'movimiento brusco', icon: '💥' },
  io_78: { cat: 'Movimiento y ubicación', label: 'exceso de velocidad', icon: '⚠️' },
  cell_id: { cat: 'Movimiento y ubicación', label: 'antena celular', icon: '🗼' },
  lac: { cat: 'Movimiento y ubicación', label: 'área celular', icon: '🛰️' },
  mcc: { cat: 'Movimiento y ubicación', label: 'código país', icon: '🌍' },
  mnc: { cat: 'Movimiento y ubicación', label: 'código operador', icon: '📶' },
  io_179: { cat: 'Movimiento y ubicación', label: 'estado GNSS', icon: '🛰️' },
  io_180: { cat: 'Movimiento y ubicación', label: 'satélites usados', icon: '🛰️' },
  io_18: { cat: 'Movimiento y ubicación', label: 'satélites visibles', icon: '🛰️' },

  // Sensores físicos
  acc_x: { cat: 'Sensores físicos', label: 'aceleración X', icon: '🌀' },
  acc_y: { cat: 'Sensores físicos', label: 'aceleración Y', icon: '🌀' },
  acc_z: { cat: 'Sensores físicos', label: 'aceleración Z', icon: '🌀' },
  io_68: { cat: 'Sensores físicos', label: 'aceleración X (int)', icon: '🌀' },
  io_72: { cat: 'Sensores físicos', label: 'aceleración Y (int)', icon: '🌀' },
  io_73: { cat: 'Sensores físicos', label: 'aceleración Z (int)', icon: '🌀' },
  shock: { cat: 'Sensores físicos', label: 'impacto', icon: '💥' },
  io_75: { cat: 'Sensores físicos', label: 'impacto (int)', icon: '💥' },
  tilt: { cat: 'Sensores físicos', label: 'inclinación', icon: '📐' },
  io_74: { cat: 'Sensores físicos', label: 'inclinación (int)', icon: '📐' },

  // Vehículo y motor (CANBus / ignición)
  ign: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'ignición', icon: '🔑' },
  io_1: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'ignición digital', icon: '🔑' },
  can_ign_key: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'llave CAN', icon: '🔑' },
  can_eng_rpm: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'RPM motor', icon: '🔧' },
  can_eng_cool_temp: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'temp refrigerante', icon: '🌡️' },
  can_total_fuel: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'combustible', icon: '⛽' },
  can_range: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'autonomía', icon: '🔋🚗' },
  can_acc_pedal_press: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'pedal acelerador', icon: '🦶' },
  can_total_eng_hours: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'horas motor', icon: '⏱️' },
  can_total_driv_time: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'tiempo conducción', icon: '🕒' },
  can_total_eng_idle: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'horas ralentí', icon: '🕰️' },
  can_total_idle_fuel: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'combustible ralentí', icon: '⛽' },
  can_axle_weight_2: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'peso eje 2', icon: '⚖️' },
  can_lights: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'luces', icon: '💡' },
  can_doors: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'puertas', icon: '🚪' },
  can_tacho_inf: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'tacógrafo', icon: '📈' },
  can_detail_inf: { cat: 'Vehículo y motor (CANBus / ignición)', label: 'info CAN extra', icon: '📊' },

  // Uso y tiempos
  hrs: { cat: 'Uso y tiempos', label: 'horas motor', icon: '⏱️' },
  rt: { cat: 'Uso y tiempos', label: 'runtime periodo', icon: '🕒' },
  wt: { cat: 'Uso y tiempos', label: 'work time', icon: '🔧' },
  it: { cat: 'Uso y tiempos', label: 'idle time', icon: '🕰️' },
  total_work_tm: { cat: 'Uso y tiempos', label: 'tiempo total de trabajo', icon: '🔧⏱️' },

  // Combustible
  fuel_lvl: { cat: 'Combustible', label: 'nivel de combustible', icon: '⛽' },
  fuel_life: { cat: 'Combustible', label: 'vida combustible', icon: '📊⛽' },
  fuel_tot: { cat: 'Combustible', label: 'combustible total', icon: '⛽📈' },
  fuel_work: { cat: 'Combustible', label: 'combustible en trabajo', icon: '⛽🔧' },
  fuel_idle: { cat: 'Combustible', label: 'combustible en ralentí', icon: '⛽🕰️' },

  // Rendimiento y consumo
  fbr_work: { cat: 'Rendimiento y consumo', label: 'tasa consumo trabajo', icon: '📉🔧' },
  fbr_idle: { cat: 'Rendimiento y consumo', label: 'tasa consumo ralentí', icon: '📉🕰️' },
  fbr_tot: { cat: 'Rendimiento y consumo', label: 'tasa consumo total', icon: '📉⛽' },

  // Distancia
  odo: { cat: 'Distancia', label: 'odómetro periodo', icon: '🛣️' },
  dist: { cat: 'Distancia', label: 'distancia trabajo', icon: '📏' },

  // Emisiones
  co2: { cat: 'Emisiones', label: 'emisiones CO₂', icon: '🌍' },

  // Energía
  pwr_ext: { cat: 'Energía', label: 'voltaje externo', icon: '🔌' },
  pwr_int: { cat: 'Energía', label: 'voltaje interno', icon: '🔋' },
  battery: { cat: 'Energía', label: 'batería interna (%)', icon: '🔋' },
  battery_mv: { cat: 'Energía', label: 'batería interna (mV)', icon: '🔋' },
  backup_battery: { cat: 'Energía', label: 'batería de respaldo', icon: '🪫' },
  chg_status: { cat: 'Energía', label: 'estado de carga', icon: '⚡️' },
  rep_interval_pwr_on: { cat: 'Energía', label: 'intervalo ON', icon: '⏱️' },
  rep_interval_pwr_off: { cat: 'Energía', label: 'intervalo OFF', icon: '⏲️' },
  io_67: { cat: 'Energía', label: 'batería interna (mV)', icon: '🔋' },

  // Red y comunicación
  csq: { cat: 'Red y comunicación', label: 'señal celular', icon: '📶' },
  gsm_status: { cat: 'Red y comunicación', label: 'estado GSM', icon: '📡' },
  sim_iccid: { cat: 'Red y comunicación', label: 'ICCID SIM', icon: '🧩' },
  sim_imsi: { cat: 'Red y comunicación', label: 'IMSI SIM', icon: '🆔' },
  buffer: { cat: 'Red y comunicación', label: 'origen buffer', icon: '💾' },
  gsm: { cat: 'Red y comunicación', label: 'señal GSM (0–5)', icon: '📶' },
  io_21: { cat: 'Red y comunicación', label: 'señal GSM', icon: '📶' },
  io_181: { cat: 'Red y comunicación', label: 'cell ID', icon: '🗼' },
  io_182: { cat: 'Red y comunicación', label: 'LAC', icon: '📡' },
  io_200: { cat: 'Red y comunicación', label: 'viaje activo', icon: '🚗' },
  io_239: { cat: 'Red y comunicación', label: 'SIM insertada', icon: '🧩' },
  io_240: { cat: 'Red y comunicación', label: 'roaming', icon: '🌍' },
  io_241: { cat: 'Red y comunicación', label: 'timestamp red', icon: '⏱️' },
  io_206: { cat: 'Red y comunicación', label: 'operador móvil (MCC/MNC)', icon: '📶' },

  // Entradas y salidas / analógicos
  adc1: { cat: 'Entradas y salidas / analógicos', label: 'entrada analógica 1', icon: '🎛️' },
  adc2: { cat: 'Entradas y salidas / analógicos', label: 'entrada analógica 2', icon: '🎛️' },
  adc3: { cat: 'Entradas y salidas / analógicos', label: 'entrada analógica 3', icon: '🎛️' },
  din_status: { cat: 'Entradas y salidas / analógicos', label: 'entrada digital', icon: '🔌' },
  dout_status: { cat: 'Entradas y salidas / analógicos', label: 'salida digital', icon: '💡' },
  io_3: { cat: 'Entradas y salidas / analógicos', label: 'salida digital 1', icon: '💡' },
  io_24: { cat: 'Entradas y salidas / analógicos', label: 'salida digital 2', icon: '💡' },

  // Eventos y reportes
  eri: { cat: 'Eventos y reportes', label: 'tipo de evento', icon: '🧾' },
  report_id: { cat: 'Eventos y reportes', label: 'ID de reporte', icon: '🏷️' },
  report_type: { cat: 'Eventos y reportes', label: 'tipo de reporte', icon: '📑' },
  event_io_id: { cat: 'Eventos y reportes', label: 'IO del evento', icon: '🎯' },
  prior: { cat: 'Eventos y reportes', label: 'prioridad', icon: '🎚️' },
  total_io: { cat: 'Eventos y reportes', label: 'total IO', icon: '🧮' },
  io_113: { cat: 'Eventos y reportes', label: 'prioridad evento', icon: '⚠️' },

  // Información del dispositivo
  dev_type: { cat: 'Información del dispositivo', label: 'tipo de dispositivo', icon: '📟' },
  proto_ver: { cat: 'Información del dispositivo', label: 'versión de protocolo', icon: '📜' },
};

function categoryEmoji(category) {
  switch ((category || '').toLowerCase()) {
    case 'truck': return '🚚';
    case 'car': return '🚗';
    case 'machine':
    case 'machinery':
    case 'equipment': return '🏗️';
    case 'bus': return '🚌';
    case 'pickup': return '🛻';
    case 'motorcycle': return '🏍️';
    case 'tractor': return '🚜';
    case 'excavator': return '⛏️';
    case 'forklift': return '🏗️';
    case 'van': return '🚐';
    case 'trailer': return '🚛';
    default: return '📍';
  }
}

function effectiveColor(device) {
  if (device.markerColor) return device.markerColor;
  if (typeof device.speed === 'number' && device.speed > 0) return '#16a34a'; // green
  return '#6b7280'; // gray
}

// Human-friendly formatting by code
function formatSecondsHuman(s) {
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return '0 s';
  if (n < 60) return `${Math.round(n)} s`;
  const m = Math.floor(n / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h} h ${rem} min` : `${h} h`;
}

function formatHoursHuman(h) {
  const n = Number(h);
  if (!Number.isFinite(n) || n <= 0) return '0 h';
  if (n < 1) return `${(n * 60).toFixed(0)} min`;
  return `${n.toFixed(2)} h`;
}

function signalQualityLabel(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (n <= 9) return 'muy mala';
  if (n <= 14) return 'regular';
  if (n <= 19) return 'buena';
  return 'excelente';
}

function bitmaskOnList(n) {
  const v = Number(n) | 0;
  const on = [];
  for (let i = 0; i < 16; i++) {
    if (v & (1 << i)) on.push(i + 1);
  }
  return on;
}

function formatValueByCode(code, value) {
  const n = Number(value);
  const isNum = Number.isFinite(n);
  switch (code) {
    // Movimiento y ubicación
    case 'moving_status': return value == 1 ? 'en movimiento' : 'detenido';
    case 'motion_status': return value == 0 ? 'sin movimiento detectable' : (value == 1 ? 'vibración leve' : 'movimiento');
    case 'io_2': return value == 1 ? 'movimiento detectado' : 'sin movimiento';
    case 'io_66': return value == 1 ? 'maniobra brusca' : 'normal';
    case 'io_78': return value == 1 ? 'exceso de velocidad' : 'dentro del límite';
    case 'gps_acc':
    case 'hdop':
    case 'pdop': return isNum ? `${n.toFixed(1)} m` : String(value);
    case 'mileage': return isNum ? `${n.toFixed(0)} m` : String(value);
    case 'io_9': return isNum ? `${n} m` : String(value);
    case 'io_16': return isNum ? `${n} pulsos` : String(value);
    case 'io_19': return isNum ? `${n}°` : String(value);
    case 'io_17': return isNum ? `${n} m` : String(value);
    case 'can_speed':
    case 'io_6': return isNum ? (n > 0 ? `${n} km/h` : 'detenido') : String(value);
    case 'can_total_distance': return isNum ? `${n} km` : String(value);
    case 'io_179': return value == 0 ? 'sin FIX' : (value == 1 ? 'fix 2D' : (value == 2 ? 'fix 3D' : `FIX (${value})`));
    case 'io_180': return isNum ? (n > 0 ? `${n} satélites` : 'sin satélites usados') : String(value);
    case 'io_18': return isNum ? (n === 65532 ? 'inválido' : `${n}`) : String(value);

    // Sensores físicos
    case 'acc_x':
    case 'acc_y':
    case 'acc_z': return isNum ? (n === 0 ? 'sin movimiento' : `${n}`) : String(value);
    case 'tilt':
    case 'io_74': return isNum ? (n === 0 ? 'sin inclinación' : `${n}°`) : String(value);
    case 'shock':
    case 'io_75': return value == 1 ? 'impacto detectado' : 'sin impacto';

    // Motor / Ignición
    case 'ign':
    case 'io_1': return value == 1 ? 'encendido' : 'apagado';
    case 'can_ign_key': return value == 1 ? 'llave ON' : 'llave OFF';
    case 'can_eng_rpm': return isNum ? (n > 0 ? `${n} RPM` : 'motor apagado') : String(value);

    // Uso y tiempos (horas / segundos)
    case 'hrs':
    case 'rt':
    case 'wt':
    case 'it': return isNum ? formatHoursHuman(n) : String(value);
    case 'total_work_tm': return isNum ? formatSecondsHuman(n) : String(value);

    // Combustible
    case 'fuel_lvl': return isNum ? `${n}%` : String(value);
    case 'fuel_life': return isNum ? `${n}` : String(value);
    case 'fuel_tot':
    case 'fuel_work':
    case 'fuel_idle': return isNum ? `${n} L` : String(value);

    // Rendimiento / consumo
    case 'fbr_work':
    case 'fbr_idle':
    case 'fbr_tot': return isNum ? `${n.toFixed(2)} L/h` : String(value);

    // Distancia
    case 'odo':
    case 'dist': return isNum ? `${n} km` : String(value);

    // Emisiones
    case 'co2': return isNum ? `${n.toFixed(2)} kg` : String(value);

    // Energía
    case 'pwr_ext': return isNum ? `${n.toFixed(2)} V` : String(value);
    case 'pwr_int': return isNum ? `${n.toFixed(2)} V` : String(value);
    case 'battery_mv':
    case 'io_67': return isNum ? `${(n / 1000).toFixed(2)} V` : String(value);
    case 'battery':
    case 'backup_battery': return isNum ? `${n}%` : String(value);
    case 'chg_status': return value == 1 ? 'cargando' : 'sin cargar';
    case 'rep_interval_pwr_on':
    case 'rep_interval_pwr_off': return isNum ? formatSecondsHuman(n) : String(value);

    // Red / GSM
    case 'csq':
    case 'gsm':
    case 'io_21': return isNum ? `${n} (${signalQualityLabel(n)})` : String(value);
    case 'gsm_status': return value == 1 ? 'registrado en red' : 'sin red';
    case 'io_239': return value == 1 ? 'SIM presente' : 'sin SIM';
    case 'io_240': return value == 1 ? 'en roaming' : 'sin roaming';
    case 'io_181': return isNum ? (n > 0 ? `${n}` : 'sin celda') : String(value);
    case 'io_182': return isNum ? (n > 0 ? `${n}` : 'sin LAC') : String(value);
    case 'io_241': return String(value);
    case 'io_206': return isNum ? `${n}` : String(value);

    // Entradas / salidas
    case 'din_status': {
      if (!isNum || n === 0) return 'todas OFF';
      const on = bitmaskOnList(n);
      return `entradas ON: ${on.join(', ')}`;
    }
    case 'dout_status': {
      if (!isNum || n === 0) return 'todas OFF';
      const on = bitmaskOnList(n);
      return `salidas ON: ${on.join(', ')}`;
    }
    case 'io_3':
    case 'io_24': return value == 1 ? 'encendida' : 'apagada';

    // Eventos y lógica
    case 'event_io_id': return isNum ? (n > 0 ? `IO ${n}` : 'sin evento') : String(value);
    case 'report_type': return value == 2 ? 'alarma' : (value == 1 ? 'evento' : 'reporte estándar');
    case 'prior': return value == 3 ? 'crítica' : (value == 2 ? 'alta' : (value == 1 ? 'media' : 'baja'));
    case 'total_io': return isNum ? `${n}` : String(value);
    case 'io_113': return isNum ? (n === 100 ? 'interno / no crítico' : `${n}`) : String(value);

    default:
      return String(value);
  }
}

function CategoryBlock({ title, items }) {
  const [open, setOpen] = React.useState(true);
  if (!items || items.length === 0) return null;
  return (
    <Box borderWidth="1px" borderRadius="lg" p={3} bg="white" boxShadow="sm">
      <HStack justify="space-between">
        <Text fontWeight="semibold">{title}</Text>
        <IconButton size="sm" colorScheme="blue" variant="ghost" aria-label="toggle" icon={open ? <ChevronDownIcon /> : <ChevronRightIcon />} onClick={() => setOpen(v => !v)} />
      </HStack>
      <Collapse in={open} animateOpacity>
        <VStack align="stretch" spacing={1} mt={2}>
          {items.map((it, idx) => (
            <HStack key={idx} justify="space-between">
              <Text color="gray.600">{it.label}</Text>
              <Text fontWeight="medium">{formatValueByCode(it.code, it.value)}</Text>
            </HStack>
          ))}
        </VStack>
      </Collapse>
    </Box>
  );
}

export default function IframeMap() {
  const toast = useToast();
  const [loading, setLoading] = React.useState(true);
  const [accountId, setAccountId] = React.useState(null);
  const [devices, setDevices] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [expandedGroups, setExpandedGroups] = React.useState({});
  const mapContainerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markersRef = React.useRef(new Map());

  // Authenticate and load devices for the account
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (!token) throw new Error('Missing token');

        const localUrl = `/api/iframeMap?token=${encodeURIComponent(token)}`;
        let resp = await fetch(localUrl);
        const ct = resp.headers.get('content-type') || '';
        let data = null;
        if (ct.includes('application/json')) {
          data = await resp.json();
        } else {
          const projectId = appFirebase && appFirebase.options ? appFirebase.options.projectId : '';
          const region = 'us-central1';
          const cfUrl = `https://${region}-${projectId}.cloudfunctions.net/api/iframeMap?token=${encodeURIComponent(token)}`;
          resp = await fetch(cfUrl);
          data = await resp.json();
        }
        if (!resp.ok || !data.ok) throw new Error(data.error || 'Auth failed');

        await signInWithDsCustomToken(data.firebaseCustomToken);
        const accId = String(data.accountId);
        setAccountId(accId);

        const devicesSnap = await getDocs(collection(db, 'inbound', accId, 'devices'));
        const items = devicesSnap.docs.map(parseDeviceSnapshot);
        items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        if (!cancelled) {
          setDevices(items);
          setSelected(items[0] || null);
        }
      } catch (err) {
        toast({ title: 'Error', description: err.message || String(err), status: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  // Load Leaflet from CDN once
  async function ensureLeaflet() {
    if (window.L) return window.L;
    const cssId = 'leaflet-css-cdn';
    const jsId = 'leaflet-js-cdn';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (!document.getElementById(jsId)) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = jsId;
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
    return window.L;
  }

  function createDivIcon(L, device) {
    const emoji = categoryEmoji(device.category);
    const color = effectiveColor(device);
    const html = `<div style="font-size:22px; line-height:22px; color:${color}; filter: drop-shadow(0 0 1px #111);">${emoji}</div>`;
    return L.divIcon({ className: 'ds-device-icon', html, iconSize: [22, 22], iconAnchor: [11, 11] });
  }

  // Initialize or update the map markers whenever devices change
  React.useEffect(() => {
    let destroyed = false;
    (async () => {
      const L = await ensureLeaflet();
      if (destroyed) return;
      if (!mapRef.current && mapContainerRef.current) {
        mapRef.current = L.map(mapContainerRef.current).setView([-33.45, -70.66], 6);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '' }).addTo(mapRef.current);
      }
      const map = mapRef.current;
      if (!map) return;
      const markers = markersRef.current;

      const bounds = L.latLngBounds([]);
      devices.forEach(d => {
        if (!(typeof d.latitude === 'number' && typeof d.longitude === 'number')) return;
        const key = d.id;
        let m = markers.get(key);
        const icon = createDivIcon(L, d);
        if (!m) {
          m = L.marker([d.latitude, d.longitude], { icon });
          m.addTo(map).on('click', () => setSelected(d));
          markers.set(key, m);
        } else {
          m.setLatLng([d.latitude, d.longitude]);
          m.setIcon(icon);
        }
        bounds.extend([d.latitude, d.longitude]);
      });

      for (const [key, m] of markers.entries()) {
        if (!devices.find(d => d.id === key && typeof d.latitude === 'number' && typeof d.longitude === 'number')) {
          map.removeLayer(m);
          markers.delete(key);
        }
      }

      if (bounds.isValid()) {
        map.fitBounds(bounds.pad(0.2), { maxZoom: 14 });
      }
    })();
    return () => { destroyed = true; };
  }, [devices]);

  // Fly to selected device on change
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !selected) return;
    if (typeof selected.latitude === 'number' && typeof selected.longitude === 'number') {
      map.flyTo([selected.latitude, selected.longitude], 15, { duration: 0.8 });
    }
  }, [selected]);

  // Build grouped tree by groupPath (first level for simplicity)
  const grouped = React.useMemo(() => {
    const searchLC = (search || '').toLowerCase();
    const filteredList = devices.filter(d => !searchLC || (d.gpsCode || '').toLowerCase().includes(searchLC) || (d.name || '').toLowerCase().includes(searchLC));
    const map = new Map();
    filteredList.forEach(d => {
      const gp = (d.groupPath || '').trim();
      const root = gp ? gp.split('/')[0] : 'Sin grupo';
      if (!map.has(root)) map.set(root, []);
      map.get(root).push(d);
    });
    return Array.from(map.entries()).map(([group, list]) => ({ group, list }));
  }, [devices, search]);

  // Details panel with editable metadata and categorized attributes
  const DetailsPane = () => {
    const [localName, setLocalName] = React.useState('');
    const [localCategory, setLocalCategory] = React.useState('truck');
    const [localColor, setLocalColor] = React.useState('');
    const [localDesc, setLocalDesc] = React.useState('');
    const [localGroup, setLocalGroup] = React.useState('');

    React.useEffect(() => {
      setLocalName(selected?.name || '');
      setLocalCategory(selected?.category || 'truck');
      setLocalColor(selected?.markerColor || '');
      setLocalDesc(selected?.description || '');
      setLocalGroup(selected?.groupPath || '');
    }, [selected]);

    if (!selected) return <Box color="gray.500">Seleccione un sensor</Box>;

    const { telemetry, telemetryDetails } = extractTelemetryBundle(selected.raw);

    const saveMeta = async () => {
      try {
        if (!accountId || !selected) return;
        const ref = doc(db, 'inbound', String(accountId), 'devices', String(selected.id));
        await setDoc(ref, {
          name: localName || null,
          category: localCategory || 'truck',
          markerColor: localColor || null,
          description: localDesc || null,
          groupPath: localGroup || null,
        }, { merge: true });
        const patch = { name: localName || '', category: localCategory || 'truck', markerColor: localColor || '', description: localDesc || '', groupPath: localGroup || '' };
        setSelected(prev => prev ? { ...prev, ...patch } : prev);
        setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, ...patch } : d));
        toast({ title: 'Guardado', status: 'success', duration: 1500 });
      } catch (e) {
        toast({ title: 'Error al guardar', description: String(e), status: 'error' });
      }
    };

    const categorized = {};
    const detailsArr = Array.isArray(telemetryDetails) ? telemetryDetails : [];
    detailsArr.forEach(d => {
      const code = String(d.sensorCode || '').trim();
      const def = ATTRIBUTE_CATALOG[code];
      const cat = def ? def.cat : 'Otros';
      if (!categorized[cat]) categorized[cat] = [];
      categorized[cat].push({ code, value: d.value, label: def ? `${def.icon} ${def.label}` : code });
    });

    const orderedCats = CATEGORY_ORDER.filter(c => categorized[c]).concat(Object.keys(categorized).filter(c => !CATEGORY_ORDER.includes(c)).sort());

    return (
      <VStack align="stretch" spacing={3}>
        <Heading size="sm">Detalles</Heading>
        {(selected.name || selected.gpsCode) && <SensorAttribute label="Nombre" value={selected.name || selected.gpsCode} />}
        {telemetry?.gpsCode && <SensorAttribute label="GPS Code" value={String(telemetry.gpsCode)} />}
        {telemetry?.date && <SensorAttribute label="Fecha" value={String(telemetry.date)} />}
        {toNumber(telemetry?.speed) != null && <SensorAttribute label="Velocidad (km/h)" value={String(telemetry.speed)} />}
        {(toNumber(telemetry?.coordY) != null && toNumber(telemetry?.coordX) != null) && (
          <SensorAttribute label="Coordenadas" value={`${telemetry.coordY}, ${telemetry.coordX}`} />
        )}

        <Divider />
        <Heading size="sm">Configuración</Heading>
        <VStack align="stretch" spacing={2}>
          <HStack>
            <Text w="120px" color="gray.500">Nombre</Text>
            <Input variant="filled" focusBorderColor="blue.400" value={localName} onChange={(e) => setLocalName(e.target.value)} />
          </HStack>
          <HStack>
            <Text w="120px" color="gray.500">Tipo</Text>
            <Select size="sm" variant="filled" focusBorderColor="blue.400" value={localCategory} onChange={(e) => setLocalCategory(e.target.value)}>
              <option value="truck">Camión 🚚</option>
              <option value="car">Auto 🚗</option>
              <option value="machine">Maquinaria 🏗️</option>
              <option value="bus">Bus 🚌</option>
              <option value="pickup">Pickup 🛻</option>
              <option value="motorcycle">Moto 🏍️</option>
              <option value="tractor">Tractor 🚜</option>
              <option value="excavator">Excavadora ⛏️</option>
              <option value="forklift">Grúa horquilla 🏗️</option>
              <option value="van">Van 🚐</option>
              <option value="trailer">Tráiler 🚛</option>
            </Select>
          </HStack>
          <HStack>
            <Text w="120px" color="gray.500">Color</Text>
            <Input type="color" value={localColor || '#2563eb'} onChange={(e) => setLocalColor(e.target.value)} w="56px" p={0} />
          </HStack>
          <HStack>
            <Text w="120px" color="gray.500">Grupo</Text>
            <Input variant="filled" focusBorderColor="blue.400" placeholder="Ej: Cliente A/Patio Norte" value={localGroup} onChange={(e) => setLocalGroup(e.target.value)} />
          </HStack>
          <HStack align="start">
            <Text w="120px" color="gray.500">Descripción</Text>
            <Textarea variant="filled" focusBorderColor="blue.400" value={localDesc} onChange={(e) => setLocalDesc(e.target.value)} rows={3} />
          </HStack>
          <HStack>
            <Button size="sm" colorScheme="blue" variant="solid" borderRadius="md" px={4} onClick={saveMeta}>Guardar</Button>
          </HStack>
        </VStack>

        <Divider />
        <Heading size="sm">Atributos</Heading>
        <VStack align="stretch" spacing={2}>
          {orderedCats.map(cat => (
            <CategoryBlock key={cat} title={cat} items={categorized[cat]} />
          ))}
        </VStack>
      </VStack>
    );
  };

  return (
    <Flex direction="column" h="100vh" bg="gray.50" p={3}>
      {loading ? (
        <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
      ) : (
        <Flex flex="1" gap={3} overflow="hidden">
          {/* Left: grouped list */}
          <Box w={{ base: '100%', md: '320px' }} bg="white" borderWidth="1px" borderRadius="lg" p={3} overflowY="auto" boxShadow="sm">
            <Input variant="filled" focusBorderColor="blue.400" placeholder="Buscar por nombre o GPS" value={search} onChange={(e) => setSearch(e.target.value)} mb={3} />
            <VStack align="stretch" spacing={2}>
              {grouped.map(({ group, list }) => {
                const open = expandedGroups[group] ?? true;
                return (
                  <Box key={group} borderWidth="1px" borderRadius="md" p={2} bg="white">
                    <HStack justify="space-between" onClick={() => setExpandedGroups(s => ({ ...s, [group]: !open }))} cursor="pointer">
                      <Text fontWeight="semibold">{group}</Text>
                      {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    </HStack>
                    <Collapse in={open} animateOpacity>
                      <List mt={2} spacing={1}>
                        {list.map(d => (
                          <ListItem key={d.id} onClick={() => setSelected(d)} cursor="pointer" _hover={{ bg: 'blue.50' }} p={2} borderRadius="md">
                            <HStack justify="space-between">
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="medium">{d.name || d.gpsCode}</Text>
                                <Text color="gray.500" fontSize="sm">{d.date || 's/f'}</Text>
                              </VStack>
                              {typeof d.speed === 'number' && <Badge colorScheme="blue">{d.speed} km/h</Badge>}
                            </HStack>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </VStack>
          </Box>

          {/* Center: map */}
          <Box flex="1" minW="300px" bg="white" borderWidth="1px" borderRadius="lg" overflow="hidden" boxShadow="sm"><Box ref={mapContainerRef} h="100%" /></Box>

          {/* Right: details */}
          <Box w={{ base: '100%', md: '380px' }} bg="white" borderWidth="1px" borderRadius="lg" p={3} overflowY="auto" boxShadow="sm">
            <DetailsPane />
          </Box>
        </Flex>
      )}
    </Flex>
  );
}


